import React, { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, HeartIcon, BeakerIcon, SparklesIcon, UserIcon, StarIcon, BookOpenIcon, ClipboardDocumentCheckIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/app',
      icon: HomeIcon,
      activeColor: 'text-accent-peach',
      bgColor: 'bg-accent-peach/10',
      shortLabel: 'Home'
    },
    { 
      name: 'Baby', 
      href: '/app/baby',
      icon: HeartIcon,
      activeColor: 'text-accent-rose',
      bgColor: 'bg-accent-rose/10',
      shortLabel: 'Baby'
    },
    { 
      name: 'Health Schedule',
      href: '/app/health-schedule',
      icon: CalendarIcon,
      activeColor: 'text-accent-mint',
      bgColor: 'bg-accent-mint/10',
      shortLabel: 'Schedule'
    },
    { 
      name: 'Baby Health', 
      href: '/app/baby-health',
      icon: BeakerIcon,
      activeColor: 'text-accent-rose',
      bgColor: 'bg-accent-rose/10',
      shortLabel: 'Health'
    },
    { 
      name: 'Wellness', 
      href: '/app/wellness',
      icon: SparklesIcon,
      activeColor: 'text-accent-lavender',
      bgColor: 'bg-accent-lavender/10',
      shortLabel: 'Wellness'
    },
    { 
      name: 'Ask AI', 
      href: '/app/learning',
      icon: BookOpenIcon,
      activeColor: 'text-accent-mint',
      bgColor: 'bg-accent-mint/10',
      shortLabel: 'AI'
    }
  ];

  const isActive = (path: string) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-bg text-white relative">
      {/* Header - Fixed at top */}
      <motion.header 
        className="fixed top-0 left-0 right-0 bg-dark-card/95 backdrop-blur-xl border-b border-gray-800 z-50"
      >
        <div className="max-w-screen-sm mx-auto px-4 flex items-center justify-between h-16">
          <div className="flex items-center space-x-3">
            <HeartIcon className="h-8 w-8 text-accent-lavender" />
            <motion.h1 
              className="text-xl font-bold bg-gradient-to-r from-accent-lavender via-accent-rose to-accent-mint bg-clip-text text-transparent"
              animate={{ 
                opacity: [0.9, 1, 0.9],
                scale: [1, 1.02, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              NurturingMom
            </motion.h1>
          </div>

          {/* Profile Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="p-2 rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <UserIcon className="h-6 w-6" />
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showProfileMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-48 bg-dark-card rounded-lg shadow-lg border border-gray-800 overflow-hidden"
                >
                  <Link
                    to="/app/profile"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    Edit Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
                  >
                    Sign Out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.header>

      {/* Main Content - Add padding for fixed header and footer */}
      <main className="flex-1 pt-16 pb-20 px-4 max-w-screen-sm mx-auto w-full">
        <Outlet />
      </main>

      {/* Footer Navigation - Fixed at bottom */}
      <motion.nav 
        className="fixed bottom-0 left-0 right-0 bg-dark-card/95 backdrop-blur-xl border-t border-gray-800 z-50"
      >
        <div className="max-w-screen-sm mx-auto px-2">
          <div className="flex justify-between">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center py-2 px-3 transition-colors
                  ${isActive(item.href) ? item.activeColor : 'text-gray-400 hover:text-white'}`}
              >
                <item.icon className="h-6 w-6" />
                <span className="text-[10px] mt-1 font-medium">
                  <span className="block sm:hidden">{item.shortLabel}</span>
                  <span className="hidden sm:block">{item.name}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </motion.nav>
    </div>
  );
}

export default Layout; 